import client from './client';

export const githubApi = {
  /**
   * Get list of GitHub repositories accessible to the user
   */
  async getRepos() {
    const { data } = await client.get('/api/github/repos');
    return data;
  },

  /**
   * Get recent commits for a repository
   */
  async getCommits(owner, repo, perPage = 10) {
    const { data } = await client.get(`/api/github/repos/${owner}/${repo}/commits`, {
      params: { per_page: perPage },
    });
    return data;
  },

  /**
   * Get tags for a repository
   */
  async getTags(owner, repo, perPage = 20) {
    const { data } = await client.get(`/api/github/repos/${owner}/${repo}/tags`, {
      params: { per_page: perPage },
    });
    return data;
  },

  /**
   * Get open pull requests across all accessible projects
   */
  async getAllPulls() {
    const { data } = await client.get('/api/github/pulls');
    return data;
  },

  /**
   * Get pull requests for a repository
   */
  async getPulls(owner, repo, state = 'open', perPage = 20) {
    const { data } = await client.get(`/api/github/repos/${owner}/${repo}/pulls`, {
      params: { state, per_page: perPage },
    });
    return data;
  },

  /**
   * Get details for a specific pull request (includes mergeable status)
   */
  async getPull(owner, repo, pullNumber) {
    const { data } = await client.get(`/api/github/repos/${owner}/${repo}/pulls/${pullNumber}`);
    return data;
  },

  /**
   * Add a comment to a pull request or issue
   */
  async addComment(owner, repo, issueNumber, body) {
    const { data } = await client.post(
      `/api/github/repos/${owner}/${repo}/issues/${issueNumber}/comments`,
      { body }
    );
    return data;
  },

  /**
   * Merge a pull request
   */
  async mergePull(owner, repo, pullNumber, options = {}) {
    const { data } = await client.put(
      `/api/github/repos/${owner}/${repo}/pulls/${pullNumber}/merge`,
      options
    );
    return data;
  },

  /**
   * Add labels to a pull request or issue
   */
  async addLabels(owner, repo, issueNumber, labels) {
    const { data } = await client.post(
      `/api/github/repos/${owner}/${repo}/issues/${issueNumber}/labels`,
      { labels }
    );
    return data;
  },

  /**
   * Compare two tags/refs and get commits between them
   */
  async compareTags(owner, repo, base, head) {
    const { data } = await client.get(
      `/api/github/repos/${owner}/${repo}/compare/${encodeURIComponent(base)}/${encodeURIComponent(head)}`
    );
    return data;
  },

  /**
   * Create a GitHub issue for a Praxis task, assigned to @copilot
   */
  async createIssueForTask(taskKey) {
    const { data } = await client.post(`/api/github/tasks/${taskKey}/create-issue`);
    return data;
  },

  /**
   * Get GitHub API rate limit status
   */
  async getRateLimit() {
    const { data } = await client.get('/api/github/rate-limit');
    return data;
  },

  /**
   * Get cached Dependabot vulnerability alerts for a repository
   */
  async getDependabotAlerts(owner, repo) {
    const { data } = await client.get(`/api/github/repos/${owner}/${repo}/dependabot/alerts`);
    return data;
  },

  /**
   * Refresh Dependabot vulnerability alerts from GitHub
   */
  async refreshDependabotAlerts(owner, repo) {
    const { data } = await client.post(
      `/api/github/repos/${owner}/${repo}/dependabot/alerts/refresh`
    );
    return data;
  },

  /**
   * Get workflows for a repository
   */
  async getWorkflows(owner, repo) {
    const { data } = await client.get(`/api/github/repos/${owner}/${repo}/workflows`);
    return data;
  },

  /**
   * Get a single workflow with file content
   */
  async getWorkflow(owner, repo, workflowId) {
    const { data } = await client.get(`/api/github/repos/${owner}/${repo}/workflows/${workflowId}`);
    return data;
  },

  /**
   * Get recent runs for a workflow
   */
  async getWorkflowRuns(owner, repo, workflowId, perPage = 10) {
    const { data } = await client.get(
      `/api/github/repos/${owner}/${repo}/workflows/${workflowId}/runs`,
      {
        params: { per_page: perPage },
      }
    );
    return data;
  },

  /**
   * Trigger a workflow dispatch
   */
  async triggerWorkflow(owner, repo, workflowId, ref, inputs = {}) {
    const { data } = await client.post(
      `/api/github/repos/${owner}/${repo}/workflows/${workflowId}/dispatches`,
      {
        ref,
        inputs,
      }
    );
    return data;
  },

  /**
   * Get branches for a repository
   */
  async getBranches(owner, repo) {
    const { data } = await client.get(`/api/github/repos/${owner}/${repo}/branches`);
    return data;
  },

  /**
   * Get container packages for a repository
   */
  async getPackages(owner, repo) {
    const { data } = await client.get(`/api/github/repos/${owner}/${repo}/packages`);
    return data;
  },

  /**
   * Get versions for a container package
   */
  async getPackageVersions(owner, repo, packageName, perPage = 20) {
    const { data } = await client.get(
      `/api/github/repos/${owner}/${repo}/packages/${encodeURIComponent(packageName)}/versions`,
      {
        params: { per_page: perPage },
      }
    );
    return data;
  },

  /**
   * Get aggregated base images from Dockerfiles across all projects
   */
  async getBaseImages() {
    const { data } = await client.get('/api/github/base-images');
    return data;
  },

  /**
   * Get Docker Hub metadata for a base image
   */
  async getBaseImageMetadata(image) {
    const { data } = await client.get(
      `/api/github/base-images/${encodeURIComponent(image)}/metadata`
    );
    return data;
  },

  /**
   * Get Architecture Decision Records from the repo's docs/adr directory
   * (default branch). Returns { adrs: [{ name, path, number, title, status, html, htmlUrl, sha }] }.
   */
  async getAdrs(owner, repo) {
    const { data } = await client.get(`/api/github/repos/${owner}/${repo}/adrs`);
    return data;
  },
};
